import { useEffect } from "react";
import { useKalenderfarben } from "../daten/haken";
import type { Farbpaar } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Die gepflegten Kalenderfarben auf die Wurzel legen.

   **Warum hier und nicht in der Maske, die sie einstellt.** Bis heute
   setzte nur das Blatt „Geschäftsarten" die Variablen — beim Klick,
   im Speicher, auf der Wurzel. Das sah aus wie eine Einstellung und
   war eine Vorschau: Beim nächsten Seitenaufruf stand wieder die
   Vorgabe da, und wer die Farbe auf einem zweiten Rechner sehen
   wollte, sah sie nicht.

   Jetzt kommen sie vom Server, gehören dem Mandanten und stehen beim
   Start da — bevor das Brett das erste Mal zeichnet.

   **Dieselben Variablennamen wie in `packages/ui/src/tokens.css`.**
   Das ist der ganze Trick: Brett, Legende und jede Probe lesen aus
   `--gf-miete-fuell` und Geschwistern, und keine einzige Zeile CSS
   muss dafür angefasst werden. Was hier nicht gesetzt wird, fällt auf
   die Vorgabe zurück — `var(--gf-miete-fuell, #E1E8ED)`.

   **Hell und dunkel sind zwei Sätze, und beide werden gesetzt.** Eine
   Farbe, die auf Hell trägt, verschwindet auf Dunkel; das Brett greift
   sich je nach Thema `-fuell`/`-kante` oder `-dunkel`/`-kante-d`
   heraus. Nur den hellen Satz zu pflegen hieße, die Hälfte der
   Benutzer im Dunkeln stehen zu lassen — im Wortsinn.

   Die Kontur (fest/Option) und das Zeichen (geplant/draußen/zurück)
   bleiben davon unberührt. **Form trägt mit, nie Farbe allein**
   (28-designkonzept.md, Leitsatz 2).

   Dieser Baustein zeichnet nichts. Er hängt in der Schale, weil er
   einen angemeldeten Kontext braucht — die Farben sind Mandantendaten.
   ══════════════════════════════════════════════════════════════════ */

const ARTEN = ["miete", "leistung"] as const;

function anlegen(art: (typeof ARTEN)[number], paar: Farbpaar): void {
  const wurzel = document.documentElement;
  wurzel.style.setProperty(`--gf-${art}-fuell`, paar.fuell);
  wurzel.style.setProperty(`--gf-${art}-kante`, paar.kante);
  wurzel.style.setProperty(`--gf-${art}-text`, paar.text);
  wurzel.style.setProperty(`--gf-${art}-dunkel`, paar.dunkel);
  wurzel.style.setProperty(`--gf-${art}-kante-d`, paar.kante_d);
}

/** Alles wieder abräumen, damit die Vorgabe aus `tokens.css` greift. */
function abraeumen(): void {
  const wurzel = document.documentElement;
  for (const art of ARTEN) {
    for (const teil of ["fuell", "kante", "text", "dunkel", "kante-d"]) {
      wurzel.style.removeProperty(`--gf-${art}-${teil}`);
    }
  }
}

export function Farbraum() {
  const { data } = useKalenderfarben();

  useEffect(() => {
    if (!data) return;
    // Gilt die Vorgabe, wird nichts gesetzt, sondern geräumt: Eine
    // Wurzelvariable mit dem Vorgabewert darin sieht genauso aus und ist
    // trotzdem etwas anderes — sie überschriebe eine Korrektur in
    // `tokens.css` stillschweigend weiter.
    if (data.vorgabe) {
      abraeumen();
      return;
    }
    for (const art of ARTEN) anlegen(art, data.farben[art]);
  }, [data]);

  return null;
}
